import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { RxCross2 } from 'react-icons/rx';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { Pagination } from '~/components/Pagination';
import {
    listSavedReports,
    deleteSavedReport,
    listSavedReportCreators,
    SavedReport,
    ReportCreator,
} from '~/services/reports';

const SUPPORTED_REPORT_TYPE = 'campaign_search';

const REPORT_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: SUPPORTED_REPORT_TYPE, label: 'Client Campaigns' },
];

const LEGACY_REPORT_TYPE_LABELS: Record<string, string> = {
    patient_campaign: 'Patient Campaign',
    campaign_request: 'Campaign Request',
    medical_professional: 'Medical Professional',
    team_member: 'Team Member',
};

const REPORT_TYPE_LABELS: Record<string, string> = {
    ...LEGACY_REPORT_TYPE_LABELS,
    ...REPORT_TYPE_OPTIONS.reduce((acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
    }, {} as Record<string, string>),
};

const isLegacyType = (reportTypeSelect: string) =>
    !!reportTypeSelect && reportTypeSelect !== SUPPORTED_REPORT_TYPE;

const formatDate = (iso: string) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString();
    } catch {
        return '';
    }
};

const parseJsonFields = (raw: string): { filters: Record<string, any>; visible_columns: string[]; report_type_select: string } => {
    try {
        const parsed = JSON.parse(raw || '{}');
        return {
            filters: parsed.filters || {},
            visible_columns: parsed.visible_columns || [],
            report_type_select: parsed.report_type_select || parsed.report_type || '',
        };
    } catch {
        return { filters: {}, visible_columns: [], report_type_select: '' };
    }
};

const SavedReports: React.FC = () => {
    const navigate = useNavigate();
    const [reports, setReports] = useState<SavedReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Applied filters (drive API call)
    const [search, setSearch] = useState('');
    const [createdBy, setCreatedBy] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');

    // Pending input values
    const [searchInput, setSearchInput] = useState('');

    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [creators, setCreators] = useState<ReportCreator[]>([]);
    const [legacyViewReport, setLegacyViewReport] = useState<SavedReport | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listSavedReports({
                page,
                search: search || undefined,
                created_by: createdBy || undefined,
                type: typeFilter || undefined,
                created_from: createdFrom || undefined,
                created_to: createdTo || undefined,
            });
            setReports(data.results);
            setTotalCount(data.count);
            setTotalPages(Math.max(1, Math.ceil(data.count / 20)));
        } catch (err: any) {
            setError(err?.message || 'Failed to load saved reports.');
        } finally {
            setLoading(false);
        }
    }, [page, search, createdBy, typeFilter, createdFrom, createdTo]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        listSavedReportCreators()
            .then(setCreators)
            .catch(() => setCreators([]));
    }, []);

    const resetPageAndApply = () => setPage(1);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        resetPageAndApply();
        setSearch(searchInput.trim());
    };

    const handleCreatedByChange = (value: string) => {
        resetPageAndApply();
        setCreatedBy(value);
    };

    const handleTypeChange = (value: string) => {
        resetPageAndApply();
        setTypeFilter(value);
    };

    const handleCreatedFromChange = (value: string) => {
        resetPageAndApply();
        setCreatedFrom(value);
    };

    const handleCreatedToChange = (value: string) => {
        resetPageAndApply();
        setCreatedTo(value);
    };

    const clearAll = () => {
        setPage(1);
        setSearch('');
        setSearchInput('');
        setCreatedBy('');
        setTypeFilter('');
        setCreatedFrom('');
        setCreatedTo('');
    };

    const hasFilters = !!(search || createdBy || typeFilter || createdFrom || createdTo);

    const handleOpen = (report: SavedReport) => {
        const { filters, visible_columns, report_type_select } = parseJsonFields(report.json_fields);

        if (isLegacyType(report_type_select)) {
            setLegacyViewReport(report);
            return;
        }

        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => params.append(key, String(v)));
            } else if (value !== undefined && value !== null && value !== '') {
                params.set(key, String(value));
            }
        });
        if (visible_columns.length) {
            params.set('visible_columns', visible_columns.join(','));
        }
        navigate(`/reports/results?${params.toString()}`);
    };

    const handleDelete = async (report: SavedReport) => {
        if (!window.confirm(`Delete saved report "${report.name}"?`)) return;
        setDeletingId(report.id);
        try {
            await deleteSavedReport(report.id);
            toast.success('Saved report deleted.');
            if (reports.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                load();
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to delete saved report.');
        } finally {
            setDeletingId(null);
        }
    };

    const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none';

    return (
        <>
            <Breadcrumb pageName='Saved Reports' />

            <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1'>
                <div className='mb-4 grid grid-cols-1 gap-3 md:grid-cols-3'>
                    <form onSubmit={handleSearchSubmit} className='relative'>
                        <FaSearch className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' size={14} />
                        <input
                            type='text'
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder='Search'
                            className={`${inputCls} pl-9`}
                        />
                    </form>
                    <select
                        value={createdBy}
                        onChange={(e) => handleCreatedByChange(e.target.value)}
                        className={inputCls}
                    >
                        <option value=''>Created By</option>
                        {creators.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                    <select
                        value={typeFilter}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        className={inputCls}
                    >
                        <option value=''>Type</option>
                        {REPORT_TYPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className='mb-6 flex flex-wrap items-center gap-3'>
                    <div className='flex items-center gap-2'>
                        <input
                            type='date'
                            value={createdFrom}
                            onChange={(e) => handleCreatedFromChange(e.target.value)}
                            placeholder='Created from'
                            className={inputCls}
                        />
                        <span className='text-sm text-gray-500'>to</span>
                        <input
                            type='date'
                            value={createdTo}
                            onChange={(e) => handleCreatedToChange(e.target.value)}
                            placeholder='Created to'
                            className={inputCls}
                        />
                    </div>
                    <div className='ml-auto flex items-center gap-2'>
                        {hasFilters && (
                            <button
                                type='button'
                                onClick={clearAll}
                                className='text-sm text-gray-500 underline hover:text-gray-700'
                            >
                                Clear filters
                            </button>
                        )}
                        <button
                            onClick={() => navigate('/new-report')}
                            className='inline-flex items-center rounded-lg bg-[#00adb3] px-4 py-2 text-sm font-medium text-white hover:bg-[#00adb3]/90'
                        >
                            <FaPlus className='mr-2' size={12} />
                            New Search
                        </button>
                    </div>
                </div>

                <p className='mb-3 text-sm text-gray-600'>
                    {loading
                        ? 'Loading…'
                        : totalCount === 0
                            ? 'No saved reports'
                            : `Showing ${reports.length} of ${totalCount}`}
                </p>

                {error && (
                    <div className='mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
                        {error}
                    </div>
                )}

                {!loading && !error && reports.length > 0 && (
                    <div className='max-w-full overflow-x-auto'>
                        <table className='w-full table-auto'>
                            <thead>
                                <tr className='bg-gray-2 text-left'>
                                    <th className='min-w-[200px] py-4 px-4 font-medium text-black xl:pl-11'>Name</th>
                                    <th className='min-w-[140px] py-4 px-4 font-medium text-black'>Type</th>
                                    <th className='min-w-[200px] py-4 px-4 font-medium text-black'>Notes</th>
                                    <th className='min-w-[140px] py-4 px-4 font-medium text-black'>Created by</th>
                                    <th className='min-w-[120px] py-4 px-4 font-medium text-black'>Created</th>
                                    <th className='py-4 px-4 font-medium text-black'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.map(report => {
                                    const { report_type_select } = parseJsonFields(report.json_fields);
                                    const typeLabel = REPORT_TYPE_LABELS[report_type_select] || report_type_select || '—';
                                    const legacy = isLegacyType(report_type_select);
                                    return (
                                        <tr key={report.id}>
                                            <td className='border-b border-[#eee] py-5 px-4 pl-9 xl:pl-11'>
                                                <button
                                                    onClick={() => handleOpen(report)}
                                                    className='text-left font-semibold text-primary hover:underline'
                                                >
                                                    {report.name}
                                                </button>
                                            </td>
                                            <td className='border-b border-[#eee] py-5 px-4 text-black'>
                                                <div className='flex flex-wrap items-center gap-2'>
                                                    <span>{typeLabel}</span>
                                                    {legacy && (
                                                        <span className='inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
                                                            Legacy
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className='border-b border-[#eee] py-5 px-4 text-black'>{report.notes || ''}</td>
                                            <td className='border-b border-[#eee] py-5 px-4 text-black'>
                                                {report.created_by_user_name || '—'}
                                            </td>
                                            <td className='border-b border-[#eee] py-5 px-4 text-black'>
                                                {formatDate(report.created_date)}
                                            </td>
                                            <td className='border-b border-[#eee] py-5 px-4'>
                                                <button
                                                    onClick={() => handleDelete(report)}
                                                    disabled={deletingId === report.id}
                                                    className='flex items-center justify-center w-8 h-8 rounded text-red-500 hover:bg-red-50 disabled:opacity-50'
                                                    title='Delete'
                                                >
                                                    <FaTrash size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && totalPages > 1 && (
                    <Pagination
                        page={page}
                        setPage={setPage}
                        pages={totalPages}
                        totalCount={totalCount}
                    />
                )}
            </div>

            {legacyViewReport && (
                <LegacyReportModal
                    report={legacyViewReport}
                    onClose={() => setLegacyViewReport(null)}
                />
            )}
        </>
    );
};

interface LegacyReportModalProps {
    report: SavedReport;
    onClose: () => void;
}

const LegacyReportModal: React.FC<LegacyReportModalProps> = ({ report, onClose }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const { filters, visible_columns, report_type_select } = parseJsonFields(report.json_fields);
    const typeLabel = REPORT_TYPE_LABELS[report_type_select] || report_type_select || '—';

    // Legacy json_fields stores filters flat at the top level; new format nests them under `filters`.
    const rawFilters = (() => {
        if (Object.keys(filters).length > 0) return filters;
        try {
            const parsed = JSON.parse(report.json_fields || '{}');
            const { report_type_select: _t, visible_columns: _c, filters: _f, ...rest } = parsed;
            return rest as Record<string, any>;
        } catch {
            return {};
        }
    })();

    const filterEntries = Object.entries(rawFilters).filter(([, v]) => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== undefined && v !== null && v !== '';
    });

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4'
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-2xl rounded-lg bg-white shadow-lg'
            >
                <button
                    className='absolute right-4 top-4 text-gray-600 hover:text-gray-900'
                    onClick={onClose}
                    aria-label='Close'
                >
                    <RxCross2 />
                </button>
                <div className='p-8'>
                    <h2 className='mb-2 text-[22px] font-bold'>{report.name}</h2>
                    <div className='mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-600'>
                        <span>{typeLabel}</span>
                        <span className='inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
                            Legacy
                        </span>
                    </div>

                    <p className='mb-5 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'>
                        This saved report was created in the previous reporting system and can no
                        longer be opened. Its filters are shown below for reference — recreate it
                        under the new "Client Campaigns" report if you still need it.
                    </p>

                    {report.notes && (
                        <div className='mb-5'>
                            <h3 className='mb-1 text-sm font-semibold text-gray-700'>Notes</h3>
                            <p className='whitespace-pre-wrap text-sm text-gray-800'>{report.notes}</p>
                        </div>
                    )}

                    <div className='mb-5'>
                        <h3 className='mb-2 text-sm font-semibold text-gray-700'>
                            Saved filters ({filterEntries.length})
                        </h3>
                        {filterEntries.length === 0 ? (
                            <p className='text-sm italic text-gray-500'>No filters set.</p>
                        ) : (
                            <div className='max-h-72 overflow-auto rounded border border-gray-200'>
                                <table className='w-full text-sm'>
                                    <tbody>
                                        {filterEntries.map(([key, value]) => (
                                            <tr key={key} className='border-b border-gray-100 last:border-b-0'>
                                                <td className='w-1/3 bg-gray-50 px-3 py-2 align-top font-mono text-xs text-gray-600'>
                                                    {key}
                                                </td>
                                                <td className='px-3 py-2 align-top text-gray-800'>
                                                    {Array.isArray(value) ? value.join(', ') : String(value)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {visible_columns.length > 0 && (
                        <div className='mb-5'>
                            <h3 className='mb-1 text-sm font-semibold text-gray-700'>Visible columns</h3>
                            <p className='font-mono text-xs text-gray-700'>{visible_columns.join(', ')}</p>
                        </div>
                    )}

                    <div className='flex justify-end'>
                        <button
                            onClick={onClose}
                            className='rounded-lg bg-[#00adb3] px-4 py-2 text-sm font-medium text-white hover:bg-[#00adb3]/90'
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SavedReports;
